import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';

export const ProductReturnRateChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance } = useBoardChartData();

    const chartData = useMemo(() => {
        if (!finance) return [];

        const salesByProduct: Record<string, { name: string; sales: number; returns: number }> = {};

        const breakdownItems = finance.detailedBreakdown || finance.breakdown || [];

        for (const item of breakdownItems) {
            const id = item.entityId as string;
            const name = (item.entityName as string) || id;
            const value = Math.abs(Number(item.value) || 0);

            if (item.type === 'REVENUE' && item.category === 'product_sale') {
                if (!salesByProduct[id]) {
                    salesByProduct[id] = { name, sales: 0, returns: 0 };
                }
                salesByProduct[id].sales += value;
            }

            if (
                item.type === 'EXPENSE' &&
                (item.category === 'product_return' || item.category === 'partial_return')
            ) {
                if (!salesByProduct[id]) {
                    salesByProduct[id] = { name, sales: 0, returns: 0 };
                }
                salesByProduct[id].returns += value;
            }
        }

        return Object.values(salesByProduct)
            .filter((p) => p.returns > 0 && p.sales > 0)
            .map((p) => ({
                name: p.name,
                returnRate: Math.round((p.returns / p.sales) * 10000) / 100,
            }))
            .sort((a, b) => b.returnRate - a.returnRate)
            .slice(0, 10);
    }, [finance]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        layout="vertical"
                        margin={{ top: 10, right: 20, bottom: 10, left: 10 }}
                    >
                        <CartesianGrid
                            horizontal={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            type="number"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => `${value}%`}
                        />
                        <YAxis
                            type="category"
                            dataKey="name"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            width={120}
                            tickFormatter={(value: string) =>
                                value.length > 18 ? `${value.slice(0, 18)}...` : value
                            }
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number) => [
                                `${value}%`,
                                t('charts.returnRate', 'Return Rate'),
                            ]}
                        />
                        <Bar
                            dataKey="returnRate"
                            fill={CHART_COLORS.danger}
                            radius={[0, 3, 3, 0]}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
